import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useSavedJobs } from "@/hooks/useSavedJobs";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {
  Bookmark, Briefcase, MapPin, Building2, BookmarkX, ExternalLink,
} from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { withUtm } from "@/lib/jobUtils";

const CRAWLER_ORG_ID = "00000000-0000-0000-0000-000000000001";

const employmentTypeLabels: Record<string, string> = {
  aprendiz: "Jovem Aprendiz",
  estagio: "Estágio",
  primeiro_emprego: "Primeiro Emprego",
  assistente: "Assistente",
  analista_junior: "Analista Junior",
  freelancer: "Freelancer",
  clt: "CLT",
  pj: "PJ",
};

const workModelLabels: Record<string, string> = {
  presencial: "Presencial",
  hibrido: "Híbrido",
  remoto: "Remoto",
};

function getInitials(name: string): string {
  const words = name.trim().split(/\s+/);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

export default function AlunoVagasSalvas() {
  const { savedJobs, isLoadingSaved, toggleSave } = useSavedJobs();

  const handleRemove = (jobId: string, title: string) => {
    toggleSave.mutate(jobId);
    toast({ title: `"${title}" removida dos salvos` });
  };

  return (
    <div className="container px-4 md:px-6 py-8 max-w-3xl">
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
        <div className="flex items-center gap-3 mb-1">
          <Bookmark className="w-6 h-6 text-primary" />
          <h1 className="font-heading text-2xl font-bold text-foreground">Vagas Salvas</h1>
        </div>
        <p className="text-muted-foreground text-sm">
          {isLoadingSaved ? "Carregando…" : `${savedJobs.length} vaga${savedJobs.length !== 1 ? "s" : ""} salva${savedJobs.length !== 1 ? "s" : ""}`}
        </p>
      </motion.div>

      {isLoadingSaved ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-5">
                <Skeleton className="h-5 w-3/4 mb-3" />
                <Skeleton className="h-4 w-1/2 mb-3" />
                <div className="flex gap-2">
                  <Skeleton className="h-6 w-16" />
                  <Skeleton className="h-6 w-20" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : savedJobs.length === 0 ? (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-16">
          <Bookmark className="h-12 w-12 text-muted-foreground/40 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">Nenhuma vaga salva ainda</h3>
          <p className="text-muted-foreground mb-6">
            Salve vagas que te interessam para revisitar depois com mais calma.
          </p>
          <Button asChild>
            <Link to="/oportunidades">Explorar oportunidades</Link>
          </Button>
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="space-y-4"
        >
          {savedJobs.map((job: any, index: number) => {
            const meta = job.crawler_metadata?.[0];
            const isCrawler = job.org_id === CRAWLER_ORG_ID;
            const companyName = meta?.company_name || job.partner_orgs?.name || "Empresa Parceira";
            const initials = getInitials(companyName);

            return (
              <motion.div
                key={job.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 * index }}
              >
                <Card className="hover:shadow-md transition-shadow">
                  <CardContent className="p-5">
                    <div className="flex items-start gap-3">
                      {/* Avatar */}
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary font-bold text-sm shrink-0 select-none">
                        {initials}
                      </div>

                      <div className="flex-1 min-w-0">
                        <Link
                          to={`/oportunidades/${job.id}`}
                          className="font-semibold text-foreground hover:text-primary transition-colors line-clamp-1"
                        >
                          {job.title}
                        </Link>
                        <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-0.5">
                          <Building2 className="h-3.5 w-3.5 shrink-0" />
                          <span className="truncate">{companyName}</span>
                        </div>

                        <div className="flex flex-wrap gap-2 mt-2">
                          {job.employment_type && (
                            <Badge variant="secondary" className="text-xs">
                              <Briefcase className="h-3 w-3 mr-1" />
                              {employmentTypeLabels[job.employment_type] || job.employment_type}
                            </Badge>
                          )}
                          {job.work_model && (
                            <Badge variant="outline" className="text-xs">
                              {workModelLabels[job.work_model] || job.work_model}
                            </Badge>
                          )}
                          {(job.location_city || job.location_state) && (
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <MapPin className="h-3 w-3" />
                              <span>{[job.location_city, job.location_state].filter(Boolean).join(", ")}</span>
                            </div>
                          )}
                        </div>

                        {job.stipend_min && (
                          <p className="text-sm font-medium text-primary mt-2">
                            {job.stipend_max && job.stipend_max !== job.stipend_min
                              ? `R$ ${job.stipend_min.toLocaleString()} – R$ ${job.stipend_max.toLocaleString()}`
                              : `R$ ${job.stipend_min.toLocaleString()}`}
                            <span className="text-xs font-normal text-muted-foreground">/mês</span>
                          </p>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex flex-col gap-2 shrink-0">
                        {isCrawler && meta?.external_apply_url ? (
                          <Button asChild size="sm" variant="outline">
                            <a href={withUtm(meta.external_apply_url)} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="h-3.5 w-3.5 mr-1" /> Ver vaga
                            </a>
                          </Button>
                        ) : (
                          <Button asChild size="sm" variant="outline">
                            <Link to={`/oportunidades/${job.id}`}>Ver vaga</Link>
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-muted-foreground hover:text-destructive"
                          onClick={() => handleRemove(job.id, job.title)}
                        >
                          <BookmarkX className="h-3.5 w-3.5 mr-1" /> Remover
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </motion.div>
      )}
    </div>
  );
}
